import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import { useCallback, useEffect, useState } from "react";
import { fetchMatchCenterConfiguration, setMatchCenterConfiguration, fetchMatchCenterSources, fetchMatchCenterContent, setMatchCenterContent } from "../../../api/matchCenter";
import MatchTable from "../matchTable";

import { useSelector } from "react-redux";
import PrivateComponent from "../../../providers/PrivateComponent/PrivateComponent";
import { PERMISSIONS } from "../../../utils/constants";
import ConfigureList from "../../configurationApi/configuration/confiugreList";
import MatchTree from "../../configurationApi/configurationTree/matchTree";

export default function MatchCenterItem({ sport }) {
  const [dialog, setDialog] = useState(false);
  const [configuration, setConfiguration] = useState(null);
  const [initialHash, setInitialHash] = useState(null);
  const [version, setVersion] = useState(null);
  const [sources, setSources] = useState(null);

  const projectId = useSelector((state) => state.project.currentProjectId);

  const createHash = useCallback((configuration, level, parentId) => {
    const hash = configuration.reduce((a, c) => {
      let childrenHash = null;
      if (c.children.length > 0) {
        childrenHash = createHash(c.children, level + 1, c.entityId);
      }
      a[`${level}-${c.entityId}`] = {
        level,
        id: c.entityId,
        parentId,
        displayOrder: c.displayOrder,
        priority: c.priority,
      };
      return { ...a, ...childrenHash };
    }, {});
    return hash;
  }, []);

  const fetchConfiguration = useCallback(async () => {
    if (!projectId) return;
    const configuration = await fetchMatchCenterConfiguration(
      projectId,
      sport.id,
    );
    const hashTable = createHash(configuration, 1, null);
    setInitialHash(hashTable);
    setConfiguration(configuration);
  }, [projectId, sport, createHash]);

  const handleGetContentKeys = useCallback(({ item }) => {
    const keys = [
      { title: "Finished", contentPostfix: 1 << 3 },
      { title: "Live", contentPostfix: 1 << 1 },
      { title: "Upcoming", contentPostfix: 1 << 2 },
    ];

    if (
      item.contentKey.includes("League") ||
      item.contentKey.includes("Competition")
    ) {
      keys.push({ title: "Schedule", contentPostfix: "Schedule" });
    }

    if (
      item.contentKey.includes("League") ||
      item.contentKey.includes("Competition") ||
      item.contentKey.includes("Sport")
    ) {
      keys.push({ title: "Yesterday", contentPostfix: "Yesterday" });
      keys.push({ title: "Today", contentPostfix: "Today" });
      keys.push({ title: "Tomorrow", contentPostfix: "Tomorrow" });
    }
    return keys;
  }, []);

  const handleGetContent = useCallback(async (contentKey) => {
    const data = await fetchMatchCenterContent(projectId, sport.id, contentKey);
    return data;
  }, [projectId, sport]);

  const handleSetContent = useCallback(async (contentKey, content) => {
    const data = await setMatchCenterContent(projectId, sport.id, contentKey, content);
    return data;
  }, [projectId, sport]);

  const fetchSources = useCallback(async () => {
    if (!projectId) return;
    const sources = await fetchMatchCenterSources(projectId, sport.id);
    setSources(sources);
  }, [projectId, sport]);

  const onSubmit = useCallback(() => {
    fetchConfiguration();
    setVersion(Date.now());
  }, [fetchConfiguration]);

  const openDialog = useCallback(() => {
    setDialog(true);
  }, []);

  const closeDialog = useCallback(() => {
    setDialog(false);
  }, []);

  useEffect(() => {
    fetchConfiguration();
    fetchSources();
  }, [fetchConfiguration, fetchSources]);

  return (
    <div style={{ padding: "25px" }}>
      <div style={{ textAlign: "center" }}>
        <h1>Match center structure</h1>
        <PrivateComponent permission={PERMISSIONS.ALLOW_WRITE_MATCH_CENTER}>
          <Button variant="contained" color="primary" onClick={openDialog}>
            Configure
          </Button>
        </PrivateComponent>
        <div>
          <Dialog fullScreen open={dialog} onClose={closeDialog}>
            <AppBar sx={{ position: "relative" }} color="transparent">
              <Toolbar>
                <Button onClick={closeDialog} color="primary">
                  Close
                </Button>
              </Toolbar>
            </AppBar>
            {sources && <ConfigureList
              projectId={projectId}
              sport={sport}
              onCloseDialog={closeDialog}
              onSubmit={onSubmit}
              initialHash={initialHash}
              configuration={configuration}
              setConfiguration={setMatchCenterConfiguration}
              sources={sources}
            />}
          </Dialog>
          <MatchTree
            projectId={projectId}
            sport={sport}
            configuration={configuration}
            getContent={handleGetContent}
            setContent={handleSetContent}
            getContentKeys={handleGetContentKeys}
          />
        </div>
      </div>
      <div style={{ textAlign: "center" }}>
        <h1>Matches</h1>
        <MatchTable projectId={projectId} sport={sport} version={version} />
      </div>
    </div>
  );
}
